import React, { useMemo, useState } from 'react'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Dropzone from 'balkerne-components/Dropzone'
import api from 'balkerne-core/api'
import { readFileBuffer } from 'balkerne-fn/readFile'
import * as xlsx from 'xlsx'
import { useSnackbar } from 'notistack'
import Page from '../components/Page'
import { usePropertyTypes } from '../hooks/locations'

type TemplateRow = {
  id: string
  source_row: number

  name: string
  customer_reference_number?: string
  type?: string

  geometry?: string
  latitude?: number
  longitude?: number

  text?: string
  country?: string
  town?: string
  street?: string
  postcode?: string

  structure_value?: number
  contents_value?: number
  revenue?: number

  error_code?: string
  error_message?: string
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const REQUIRED_HEADERS = [
  'name',
  'customer_reference_number',
  'type',
  'geometry',
  'latitude',
  'longitude',
  'text',
  'country',
  'town',
  'street',
  'postcode',
  'structure_value',
  'contents_value',
  'revenue',
] as const

function asTrimmedString(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function asOptionalString(value: unknown): string | undefined {
  const s = asTrimmedString(value)
  return s ? s : undefined
}

function asOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  const parsed = Number(String(value).trim())
  return Number.isFinite(parsed) ? parsed : undefined
}

function toCreatePayload(
  row: TemplateRow,
  propertyTypes?: Array<{ id: string; label: string }>,
): Record<string, unknown> {
  const typeLabel = asOptionalString(row.type)
  const typeId = typeLabel
    ? propertyTypes?.find(t => (t.label || '').toLowerCase() === typeLabel.toLowerCase())?.id
    : undefined

  return {
    name: asTrimmedString(row.name),
    customer_reference_number: asOptionalString(row.customer_reference_number),
    type_id: typeId,

    geometry: asOptionalString(row.geometry),
    latitude: asOptionalNumber(row.latitude),
    longitude: asOptionalNumber(row.longitude),

    text: asOptionalString(row.text),
    country: asOptionalString(row.country),
    town: asOptionalString(row.town),
    postcode: asOptionalString(row.postcode),
    street: asOptionalString(row.street),

    structure_value: asOptionalNumber(row.structure_value),
    contents_value: asOptionalNumber(row.contents_value),
    revenue: asOptionalNumber(row.revenue),
  }
}

function buildTemplateRowsFromWorkbook(workbook: xlsx.WorkBook, fileName: string): TemplateRow[] {
  const sheet = workbook.Sheets?.Properties ?? workbook.Sheets?.[workbook.SheetNames?.[0] ?? '']
  if (!sheet) throw new Error('Could not find the "Properties" worksheet.')

  const rows: unknown[][] = xlsx.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  if (rows.length < 2) return []

  const headers = (rows[0] ?? []).map(h => asTrimmedString(h))
  const headerIndex = new Map<string, number>()
  headers.forEach((h, idx) => {
    if (h) headerIndex.set(h, idx)
  })

  const missing = REQUIRED_HEADERS.filter(h => !headerIndex.has(h))
  if (missing.length > 0) {
    throw new Error(`Template is missing required column(s): ${missing.join(', ')}`)
  }

  const dataRows = rows.slice(1)
  const templateRows: TemplateRow[] = []

  dataRows.forEach((r, idx) => {
    const isEmpty = (r ?? []).every(cell => asTrimmedString(cell) === '')
    if (isEmpty) return

    const sourceRow = idx + 2 // Header is row 1
    const cell = (header: string) => (r ?? [])[headerIndex.get(header) ?? -1]

    templateRows.push({
      id: `${fileName}:${sourceRow}`,
      source_row: sourceRow,
      name: asTrimmedString(cell('name')),
      customer_reference_number: asOptionalString(cell('customer_reference_number')),
      type: asOptionalString(cell('type')),
      geometry: asOptionalString(cell('geometry')),
      latitude: asOptionalNumber(cell('latitude')),
      longitude: asOptionalNumber(cell('longitude')),
      text: asOptionalString(cell('text')),
      country: asOptionalString(cell('country')),
      town: asOptionalString(cell('town')),
      street: asOptionalString(cell('street')),
      postcode: asOptionalString(cell('postcode')),
      structure_value: asOptionalNumber(cell('structure_value')),
      contents_value: asOptionalNumber(cell('contents_value')),
      revenue: asOptionalNumber(cell('revenue')),
    })
  })

  return templateRows
}

function EditFailedRowDialog({
  open,
  row,
  onClose,
  onSave,
}: {
  open: boolean
  row: TemplateRow | null
  onClose: () => void
  onSave: (row: TemplateRow) => void
}) {
  const [draft, setDraft] = useState<TemplateRow | null>(row)

  React.useEffect(() => setDraft(row), [row])

  if (!draft) return null

  const update = (patch: Partial<TemplateRow>) => setDraft({ ...draft, ...patch })

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit failed row (template row {draft.source_row})</DialogTitle>
      <DialogContent>
        <Stack direction="column" gap={2} sx={{ mt: 1 }}>
          <Stack direction="row" gap={2}>
            <TextField
              label="Name"
              fullWidth
              value={draft.name ?? ''}
              onChange={e => update({ name: e.target.value })}
            />
            <TextField
              label="Customer reference number"
              fullWidth
              value={draft.customer_reference_number ?? ''}
              onChange={e => update({ customer_reference_number: e.target.value })}
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <TextField
              label="Type (label)"
              fullWidth
              value={draft.type ?? ''}
              onChange={e => update({ type: e.target.value })}
            />
            <TextField
              label="Country"
              fullWidth
              value={draft.country ?? ''}
              onChange={e => update({ country: e.target.value })}
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <TextField
              label="Town"
              fullWidth
              value={draft.town ?? ''}
              onChange={e => update({ town: e.target.value })}
            />
            <TextField
              label="Postcode"
              fullWidth
              value={draft.postcode ?? ''}
              onChange={e => update({ postcode: e.target.value })}
            />
          </Stack>

          <TextField
            label="Street"
            fullWidth
            value={draft.street ?? ''}
            onChange={e => update({ street: e.target.value })}
          />

          <TextField
            label="Address text"
            fullWidth
            value={draft.text ?? ''}
            onChange={e => update({ text: e.target.value })}
          />

          <Stack direction="row" gap={2}>
            <TextField
              label="Latitude"
              fullWidth
              value={draft.latitude ?? ''}
              onChange={e => update({ latitude: asOptionalNumber(e.target.value) })}
            />
            <TextField
              label="Longitude"
              fullWidth
              value={draft.longitude ?? ''}
              onChange={e => update({ longitude: asOptionalNumber(e.target.value) })}
            />
          </Stack>

          <TextField
            label="Geometry (WKT or GeoJSON)"
            fullWidth
            multiline
            minRows={4}
            value={draft.geometry ?? ''}
            onChange={e => update({ geometry: e.target.value })}
          />

          <Stack direction="row" gap={2}>
            <TextField
              label="Structure value"
              fullWidth
              value={draft.structure_value ?? ''}
              onChange={e => update({ structure_value: asOptionalNumber(e.target.value) })}
            />
            <TextField
              label="Contents value"
              fullWidth
              value={draft.contents_value ?? ''}
              onChange={e => update({ contents_value: asOptionalNumber(e.target.value) })}
            />
            <TextField
              label="Revenue"
              fullWidth
              value={draft.revenue ?? ''}
              onChange={e => update({ revenue: asOptionalNumber(e.target.value) })}
            />
          </Stack>

          {draft.error_message && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: theme => `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                backgroundColor: theme => alpha(theme.palette.error.main, 0.06),
              }}>
              <Typography variant="subtitle2">Error</Typography>
              <Typography variant="body2" color="text.secondary">
                {draft.error_code ? `${draft.error_code}: ` : ''}
                {draft.error_message}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSave(draft)
            onClose()
          }}
          variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function TemplateAddProperties() {
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const { data: propertyTypes } = usePropertyTypes()

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const [fileName, setFileName] = useState<string | null>(null)
  const [rows, setRows] = useState<TemplateRow[]>([])
  const [submitting, setSubmitting] = useState(false)

  const [editingRowId, setEditingRowId] = useState<string | null>(null)

  const editingRow = useMemo(() => rows.find(r => r.id === editingRowId) ?? null, [rows, editingRowId])

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'source_row', headerName: 'Row', width: 80 },
      { field: 'name', headerName: 'Name', minWidth: 200, flex: 1 },
      { field: 'customer_reference_number', headerName: 'CRN', minWidth: 160 },
      { field: 'type', headerName: 'Type', minWidth: 160 },
      { field: 'country', headerName: 'Country', minWidth: 120 },
      { field: 'town', headerName: 'Town', minWidth: 160 },
      { field: 'street', headerName: 'Street', minWidth: 220, flex: 1 },
      { field: 'postcode', headerName: 'Postcode', minWidth: 120 },
      { field: 'text', headerName: 'Address text', minWidth: 220, flex: 1 },
      { field: 'latitude', headerName: 'Lat', minWidth: 120 },
      { field: 'longitude', headerName: 'Lon', minWidth: 120 },
      { field: 'geometry', headerName: 'Geometry', minWidth: 220, flex: 1 },
      { field: 'structure_value', headerName: 'Structure', minWidth: 120 },
      { field: 'contents_value', headerName: 'Contents', minWidth: 120 },
      { field: 'revenue', headerName: 'Revenue', minWidth: 120 },
      {
        field: 'error_message',
        headerName: 'Error',
        minWidth: 320,
        flex: 1,
        valueGetter: params => {
          const code = params.row?.error_code
          const msg = params.row?.error_message
          return msg ? (code ? `${code}: ${msg}` : msg) : ''
        },
      },
      {
        field: '__actions',
        headerName: '',
        width: 64,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const canEdit = Boolean(params.row?.error_code) && !submitting
          return (
            <Tooltip title={canEdit ? 'Edit failed row' : 'Submit to see failures'} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={e => {
                    e.stopPropagation()
                    setEditingRowId(params.row.id)
                  }}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )
        },
      },
    ],
    [submitting],
  )

  const handleDownloadTemplate = async () => {
    setDownloading(true)
    try {
      const res = await api.get('/locations/upload-template', { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] ?? XLSX_MIME })
      const url = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = 'upload-template-smartresilience_PREVIEW.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Failed to download template', { variant: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const handleTemplateSelected = async (file: File | undefined) => {
    if (!file) return
    setParsing(true)
    try {
      const data = await readFileBuffer(file)
      const workbook = xlsx.read(data.data)
      const templateRows = buildTemplateRowsFromWorkbook(workbook, file.name)

      setFileName(file.name)
      setRows(templateRows)
      setEditingRowId(null)
      enqueueSnackbar(`Loaded ${templateRows.length} row(s) from template`, { variant: 'success' })
      setUploadDialogOpen(false)
    } catch (err: any) {
      console.error(err)
      enqueueSnackbar(err?.message ?? 'Failed to read template', { variant: 'error' })
    } finally {
      setParsing(false)
    }
  }

  const handleSubmit = async () => {
    if (rows.length === 0) {
      enqueueSnackbar('Upload a completed template first', { variant: 'info' })
      return
    }

    setSubmitting(true)

    const previousRows = rows
    try {
      // Clear previous failures (removes failed state on repeat submit)
      setRows(prev => prev.map(r => ({ ...r, error_code: undefined, error_message: undefined })))

      const validationFailures: TemplateRow[] = []
      const toSubmit: TemplateRow[] = []

      previousRows.forEach(r => {
        if (!asTrimmedString(r.name)) {
          validationFailures.push({
            ...r,
            error_code: 'validation_error',
            error_message: 'Name is required.',
          })
        } else {
          toSubmit.push(r)
        }
      })

      if (toSubmit.length === 0) {
        setRows(validationFailures)
        enqueueSnackbar('Fix the errors and try again', { variant: 'warning' })
        return
      }

      const payload = toSubmit.map(r => toCreatePayload(r, propertyTypes))
      const res = await api.post('/locations/create', { properties: payload })

      const failures: Array<{ index: number; code: string; message: string }> = res.data?.failures ?? []
      const failureByIndex = new Map<number, { code: string; message: string }>()
      failures.forEach(f => {
        if (typeof f?.index === 'number') failureByIndex.set(f.index, { code: f.code, message: f.message })
      })

      const serverFailures: TemplateRow[] = []
      toSubmit.forEach((r, idx) => {
        const failure = failureByIndex.get(idx)
        if (!failure) return
        serverFailures.push({
          ...r,
          error_code: failure.code,
          error_message: failure.message,
        })
      })

      const successCount = Number(res.data?.success_count ?? toSubmit.length - failures.length)
      const totalFailureCount = validationFailures.length + serverFailures.length

      enqueueSnackbar(
        `Created ${successCount} propert${successCount === 1 ? 'y' : 'ies'}. ${totalFailureCount} failed.`,
        {
          variant: totalFailureCount > 0 ? 'warning' : 'success',
        },
      )

      // Remove successful rows; keep only failures
      setRows([...validationFailures, ...serverFailures])
    } catch (err) {
      console.error(err)
      setRows(previousRows)
      enqueueSnackbar('Upload failed. Please try again.', { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Page title="Import Properties (Preview)" back>
      <Stack direction="column" gap={2}>
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: theme => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
          }}>
          <Typography variant="subtitle1" fontWeight={600}>
            How it works
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Download the template, fill it in locally, then upload it here.
            <br />
            Successfully uploaded rows are removed while failed ones remain with an error message, so they can be edited
            and resubmitted.
          </Typography>
          <Stack direction="row" gap={1.5} sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              disabled={downloading}
              onClick={handleDownloadTemplate}>
              Download template
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={() => setUploadDialogOpen(true)}
              disabled={parsing || submitting}>
              Import template
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={rows.length === 0 || submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {fileName ? `${fileName} • ${rows.length} row(s)` : 'No template uploaded'}
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            height: 620,
            borderRadius: 1,
            border: theme => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            overflow: 'hidden',
            '& .row--failed': {
              backgroundColor: alpha(theme.palette.error.main, 0.06),
            },
          }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            pagination
            getRowClassName={params => (params.row?.error_code ? 'row--failed' : '')}
            disableSelectionOnClick
            rowsPerPageOptions={[25, 50, 100]}
            pageSize={25}
          />
        </Box>
      </Stack>

      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload spreadsheet</DialogTitle>
        <DialogContent>
          <Stack direction="column" gap={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select the XLSX template you filled in. We’ll read the rows from the <code>Properties</code> sheet, then
              you can submit them for creation. Successful rows are removed; failed rows stay with error details.
            </Typography>
            <Box sx={{ minHeight: 120 }}>
              <Dropzone
                accept={XLSX_MIME}
                multiple={false}
                onChange={files => {
                  if (parsing) return
                  handleTemplateSelected(files?.[0])
                }}
              />
            </Box>
            {parsing && (
              <Typography variant="body2" color="text.secondary">
                Reading template…
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} variant="outlined" disabled={parsing}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <EditFailedRowDialog
        open={Boolean(editingRowId)}
        row={editingRow}
        onClose={() => setEditingRowId(null)}
        onSave={updated => {
          setRows(prev => prev.map(r => (r.id === updated.id ? updated : r)))
        }}
      />
    </Page>
  )
}
